import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthSession } from '@/api';
import { formatMainBalance, playerBalanceBreakdown } from '@/domain';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui';

export interface MenuBalanceCardProps {
  className?: string;
}

export function MenuBalanceCard({ className }: MenuBalanceCardProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { user } = useAuthSession();
  const [expanded, setExpanded] = useState(false);
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const mainBalance = formatMainBalance(user);
  const rows = playerBalanceBreakdown(user);

  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-md border border-border-default bg-bg-surface',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className="flex w-full flex-col gap-2 px-3 py-3 text-left"
      >
        <div className="flex w-full items-center justify-between">
          <span className="inline-flex items-center rounded-full bg-action-primary px-[6px] py-0.5 text-[9px] leading-none font-bold tracking-[0.6px] text-text-on-action uppercase whitespace-nowrap">
            {label('Main Balance', 'MAIN BALANCE')}
          </span>
          <Icon
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={16}
            className="text-icon-default"
          />
        </div>
        <span className="text-[22px] leading-none font-bold tabular-nums text-text-primary">
          {mainBalance}
        </span>
      </button>

      {expanded && (
        <div className="flex flex-col gap-3 px-3 pb-3">
          <div className="h-px w-full bg-border-subtle" />
          <span className="text-[10px] font-bold tracking-[0.8px] text-text-secondary uppercase">
            {label('BREAKDOWN', 'BREAKDOWN')}
          </span>
          <ul className="flex flex-col gap-1">
            {rows.map((row) => (
              <li key={row.id} className="flex items-center gap-3 py-1">
                <span className="min-w-0 flex-1 text-[11px] leading-4 text-text-secondary">
                  {label(row.label, row.label)}
                </span>
                <span className="shrink-0 text-right text-[12px] leading-4 font-bold tabular-nums text-text-primary">
                  {row.formatted}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
